import React, { useState, useEffect, useRef, useCallback } from 'react';
import { schoolsApi, bookingsApi } from '../services/api';
import { useAuth } from '../App';
import SchoolCard   from '../components/SchoolCard';
import SchoolDetail from '../components/SchoolDetail';
import BookingForm  from '../components/BookingForm';
import AuthModal    from '../components/AuthModal';

export default function HomePage({ user, onLogin, onDashboardClick }) {
  const [schools,       setSchools]       = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [schoolsError,  setSchoolsError]  = useState('');
  const [bookSchool,    setBookSchool]    = useState(null);
  const [bookLoading,   setBookLoading]   = useState(false);
  const [menuOpen,      setMenuOpen]      = useState(false);
  const headerRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => {
      if (headerRef.current && !headerRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const [authModal,     setAuthModal]     = useState({ open: false, view: 'login' });
  const [pendingSchool, setPendingSchool] = useState(null);
  const [pendingUser,   setPendingUser]   = useState(null);
  const [activeSection, setActiveSection] = useState('home');
  const [viewSchool,    setViewSchool]    = useState(null);
  const [myBookings,    setMyBookings]    = useState([]);
  const [bkLoading,     setBkLoading]     = useState(false);
  const [bkError,       setBkError]       = useState('');
  const [livePopup,     setLivePopup]     = useState(null);

  function toEmbedUrl(url) {
    if (!url) return '';
    let m = url.match(/youtube\.com\/watch\?(?:.*&)?v=([^&]+)/);
    if (m) return `https://www.youtube.com/embed/${m[1]}?autoplay=1`;
    m = url.match(/youtu\.be\/([^?&]+)/);
    if (m) return `https://www.youtube.com/embed/${m[1]}?autoplay=1`;
    m = url.match(/youtube\.com\/live\/([^?&]+)/);
    if (m) return `https://www.youtube.com/embed/${m[1]}?autoplay=1`;
    return url;
  }

  const { logout } = useAuth();
  const activeUser = user || pendingUser;

  // Mask phone: 9876543210 → 98765 XXXXX
  const maskedPhone = (phone) => phone ? phone.slice(0, 5) + 'XXXXX' : '';

  useEffect(() => {
    schoolsApi.getAll('active')
      .then(d  => setSchools(d.schools || []))
      .catch(() => setSchoolsError('Unable to load schools. Please try again later.'))
      .finally(() => setLoading(false));
  }, []);

  // Reset to home view whenever user logs out
  useEffect(() => {
    if (!user) {
      setActiveSection('home');
      setMyBookings([]);
      setViewSchool(null);
      setLivePopup(null);
    }
  }, [user]);

  const openAuth  = (view = 'login') => setAuthModal({ open: true, view });
  const closeAuth = () => setAuthModal(a => ({ ...a, open: false }));

  const handleLoginSuccess = (userData) => {
    if (userData.userType === 'parent' && pendingSchool) {
      setPendingUser(userData);
      setBookSchool(pendingSchool);
      setPendingSchool(null);
    } else if (userData.userType !== 'parent') {
      // admin / school — go straight to their dashboard
      setPendingSchool(null);
      onLogin(userData);
    } else {
      onLogin(userData);
    }
  };

  const handleBook = (school) => {
    setViewSchool(null);
    if (!activeUser) { setPendingSchool(school); openAuth('login'); return; }
    if (activeUser.userType !== 'parent') return;
    setBookSchool(school);
  };

  const handleViewDetails = (school) => {
    setViewSchool(school);
    if (activeUser?.userType === 'parent') loadMyBookings();
  };

  const submitBooking = async (form) => {
    setBookLoading(true);
    try {
      await bookingsApi.create({
        parentId: activeUser.userId,
        schoolId: bookSchool.id || bookSchool.schoolId,
        ...form,
      });
      setBookSchool(null);
      if (pendingUser) {
        onLogin(pendingUser);
        setPendingUser(null);
      }
      setActiveSection('bookings');
    } catch (err) {
      alert(err.message);
    } finally {
      setBookLoading(false);
    }
  };

  const loadMyBookings = useCallback(async () => {
    if (!activeUser?.userId) return;
    setBkLoading(true); setBkError('');
    try {
      const d = await bookingsApi.getAll({ userId: activeUser.userId, userType: 'parent' });
      const list = (d.bookings || []).sort((a, b) =>
        new Date(b.createdAt || b.date) - new Date(a.createdAt || a.date)
      );
      setMyBookings(list);
    } catch (e) {
      setBkError('Could not load bookings. Please try again.');
    } finally {
      setBkLoading(false);
    }
  }, [activeUser?.userId]);

  useEffect(() => {
    if (activeSection === 'bookings') loadMyBookings();
  }, [activeSection, loadMyBookings]);

  const cancelBooking = async (bookingId) => {
    if (!window.confirm('Cancel this booking?')) return;
    try {
      await bookingsApi.cancel({ bookingId, parentId: activeUser.userId });
      loadMyBookings();
    } catch (e) { alert(e.message); }
  };

  const STATUS_COLOR = { pending: '#f59e0b', confirmed: '#10b981', cancelled: '#ef4444', completed: '#6366f1' };
  const STATUS_LABEL = { pending: 'Pending', confirmed: 'Confirmed', cancelled: 'Cancelled', completed: 'Completed' };

  const canBook  = !activeUser || activeUser.userType === 'parent';

  return (
    <div className="home-page">

      {/* ── Sticky Header ── */}
      <header className="home-header" ref={headerRef}>
        <div className="home-header-inner">
          <div className="home-logo">
            <img
              src="https://firebasestorage.googleapis.com/v0/b/daycare-39d32.firebasestorage.app/o/images%2Flogo_la.png?alt=media"
              alt="LittleAura"
              className="home-logo-img"
            />
            <div className="home-logo-wordmark">
              <span className="home-logo-word1">Little</span><span className="home-logo-word2">Aura</span>
            </div>
          </div>

          <button className="home-hamburger" onClick={() => setMenuOpen(m => !m)} aria-label="Toggle menu">
            <span /><span /><span />
          </button>

          <nav className={`home-nav${menuOpen ? ' open' : ''}`}>
            <div className="home-nav-links">
              <button className={`home-nav-link${activeSection === 'home'       ? ' active' : ''}`} onClick={() => { setActiveSection('home');       setMenuOpen(false); }}>Home</button>
              <button className={`home-nav-link${activeSection === 'preschools' ? ' active' : ''}`} onClick={() => { setActiveSection('preschools'); setMenuOpen(false); }}>Pre Schools</button>
              <button className={`home-nav-link${activeSection === 'about'      ? ' active' : ''}`} onClick={() => { setActiveSection('about');      setMenuOpen(false); }}>About Us</button>
              <button className={`home-nav-link${activeSection === 'partner'    ? ' active' : ''}`} onClick={() => { setActiveSection('partner');    setMenuOpen(false); }}>Partner Program</button>
              <button className={`home-nav-link${activeSection === 'contact'    ? ' active' : ''}`} onClick={() => { setActiveSection('contact');    setMenuOpen(false); }}>Contact Us</button>
              {activeUser?.userType === 'parent' && (
                <button className={`home-nav-link home-nav-link-bookings${activeSection === 'bookings' ? ' active' : ''}`} onClick={() => { setActiveSection('bookings'); setMenuOpen(false); }}>📅 My Bookings</button>
              )}
            </div>
            <div className="home-nav-items">
              {activeUser ? (
                <>
                  <span className="home-nav-greeting">
                    Hi {maskedPhone(activeUser.phone)}
                  </span>
                  {activeUser.userType !== 'parent' && (
                    <button className="btn btn-primary btn-sm" onClick={() => { onDashboardClick(); setMenuOpen(false); }}>
                      My Dashboard
                    </button>
                  )}
                  <button className="home-logout-btn" title="Logout" onClick={() => { logout(); setMenuOpen(false); }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                  </button>
                </>
              ) : (
                <>
                  <button className="btn btn-ghost btn-sm" onClick={() => { openAuth('login'); setMenuOpen(false); }}>Sign In</button>
                  <button className="btn btn-primary btn-sm" onClick={() => { openAuth('register'); setMenuOpen(false); }}>Register Free</button>
                </>
              )}
            </div>
          </nav>
        </div>
      </header>

      {/* ══ HOME ══ */}
      {activeSection === 'home' && (
      <section id="home" className="home-section-wrap">
        <div className="home-hero-simple">
          <div className="home-hero-content">
            <div className="home-vm-badge">❤️ Safe. Nurturing. Transparent.</div>
            <h1 className="home-vm-title">A Second Home<br />for Every Child</h1>
            <p className="home-hero-sub">Discover premium daycare and preschool centres that provide CCTV-monitored, hygienic and loving environments — book your child's spot in minutes.</p>
            <button className="home-hero-cta" onClick={() => setActiveSection('preschools')}>Book a Slot &nbsp;→</button>
          </div>
          <div className="home-hero-image-wrap">
            <img
              src="https://firebasestorage.googleapis.com/v0/b/daycare-39d32.firebasestorage.app/o/images%2Fdaycare1.png?alt=media"
              alt="Happy children at daycare"
              className="home-hero-img"
            />
          </div>
        </div>

        {/* ── What Makes LittleAura Special ── */}
        <div className="home-features-section">
          <div className="home-features-inner">

            <div className="home-features-block">
              <h2 className="home-features-title">What Makes LittleAura Special</h2>
              <div className="home-features-grid">
                <div className="home-feature-item">
                  <div className="home-feature-icon-wrap" style={{ borderColor: '#e879a0', color: '#e879a0' }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="28" height="28"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
                  </div>
                  <span>CCTV Access</span>
                </div>
                <div className="home-feature-item">
                  <div className="home-feature-icon-wrap" style={{ borderColor: '#f59e0b', color: '#f59e0b' }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="28" height="28"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg>
                  </div>
                  <span>Excellence<br />in Safety</span>
                </div>
                <div className="home-feature-item">
                  <div className="home-feature-icon-wrap" style={{ borderColor: '#f97316', color: '#f97316' }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="28" height="28"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                  </div>
                  <span>Trained &<br />Caring Staff</span>
                </div>
                <div className="home-feature-item">
                  <div className="home-feature-icon-wrap" style={{ borderColor: '#14b8a6', color: '#14b8a6' }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="28" height="28"><circle cx="12" cy="8" r="3"/><path d="M12 14c-5 0-7 2-7 3v1h14v-1c0-1-2-3-7-3z"/><path d="M17 6c1.5.5 2.5 2 2.5 3.5S18.5 13 17 14"/><path d="M7 6C5.5 6.5 4.5 8 4.5 9.5S5.5 13 7 14"/></svg>
                  </div>
                  <span>Premium Hygienic<br />Environment</span>
                </div>
                <div className="home-feature-item">
                  <div className="home-feature-icon-wrap" style={{ borderColor: '#10b981', color: '#10b981' }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="28" height="28"><rect x="2" y="2" width="9" height="9" rx="1"/><rect x="13" y="2" width="9" height="9" rx="1"/><rect x="2" y="13" width="9" height="9" rx="1"/><rect x="13" y="13" width="9" height="9" rx="1"/></svg>
                  </div>
                  <span>Activities &<br />Engagement</span>
                </div>
                <div className="home-feature-item">
                  <div className="home-feature-icon-wrap" style={{ borderColor: '#f97316', color: '#f97316' }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="28" height="28"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                  </div>
                  <span>A Second<br />Home</span>
                </div>
              </div>
            </div>

            <div className="home-features-block home-features-block-parents">
              <h2 className="home-features-title">For Parents</h2>
              <div className="home-features-for-parents">
                <div className="home-fp-item">
                  <div className="home-fp-icon" style={{ borderColor: '#7c3aed', color: '#7c3aed' }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="26" height="26"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="8" y1="14" x2="10" y2="14"/><line x1="12" y1="14" x2="16" y2="14"/></svg>
                  </div>
                  <span>Easy Booking<br />&amp; Scheduling</span>
                </div>
                <div className="home-fp-divider" />
                <div className="home-fp-item">
                  <div className="home-fp-icon" style={{ borderColor: '#f59e0b', color: '#f59e0b' }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="26" height="26"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                  </div>
                  <span>Real-time Updates<br />&amp; Notifications</span>
                </div>
                <div className="home-fp-divider" />
                <div className="home-fp-item">
                  <div className="home-fp-icon" style={{ borderColor: '#14b8a6', color: '#14b8a6' }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="26" height="26"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                  </div>
                  <span>Activity &amp; Meal<br />Updates</span>
                </div>
                <div className="home-fp-divider" />
                <div className="home-fp-item">
                  <div className="home-fp-icon" style={{ borderColor: '#374151', color: '#374151' }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="26" height="26"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                  </div>
                  <span>Trusted Preschool<br />Network</span>
                </div>
                <div className="home-fp-divider" />
                <div className="home-fp-item">
                  <div className="home-fp-icon" style={{ borderColor: '#f97316', color: '#f97316' }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" width="26" height="26"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg>
                  </div>
                  <span>Verified &amp;<br />Reviewed Centers</span>
                </div>
              </div>
            </div>

          </div>
        </div>

      </section>
      )}

      {/* ══ PRE SCHOOLS ══ */}
      {activeSection === 'preschools' && !viewSchool && (
      <section className="home-section-wrap">
        <div className="home-schools-inner">
          <div className="home-section-header">
            <h2 className="home-section-title">Trusted Pre Schools Near You</h2>
          </div>
          {loading && <div className="home-loader"><div className="home-spinner" /><p>Loading centres…</p></div>}
          {schoolsError && <div className="home-msg-box home-msg-error">{schoolsError}</div>}
          <div className="home-schools-grid">
            {schools.map(s => (
              <SchoolCard key={s.id || s.schoolId} school={s} onBook={canBook ? handleBook : undefined} onViewDetails={handleViewDetails} />
            ))}
          </div>
        </div>
      </section>
      )}

      {/* ══ SCHOOL DETAIL ══ */}
      {activeSection === 'preschools' && viewSchool && (
        <SchoolDetail
          school={viewSchool}
          onBack={() => setViewSchool(null)}
          onBook={handleBook}
          canBook={canBook}
          activeUser={activeUser}
          myBookings={myBookings}
        />
      )}

      {/* ══ MY BOOKINGS ══ */}
      {activeSection === 'bookings' && (
      <section className="home-section-wrap">
        <div className="home-schools-inner">
          <div className="home-section-header">
            <h2 className="home-section-title">My Bookings</h2>
          </div>

          {bkLoading && <div className="home-loader"><div className="home-spinner" /><p>Loading…</p></div>}
          {bkError   && <div className="home-msg-box home-msg-error">{bkError}</div>}

          {!bkLoading && !bkError && myBookings.length === 0 && (
            <div className="home-msg-box" style={{ textAlign: 'center', padding: 40 }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>📭</div>
              <p style={{ fontSize: 16, color: '#6b7280' }}>You haven't booked any slot till now.</p>
              <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => setActiveSection('preschools')}>Browse Pre Schools</button>
            </div>
          )}

          <div className="my-bookings-list">
            {myBookings.map(b => {
              const canCancel = b.status === 'pending';
              const statusColor = STATUS_COLOR[b.status] || '#6b7280';
              return (
                <div key={b.id || b.bookingId} className="my-booking-card" style={{ borderLeft: `4px solid ${statusColor}` }}>
                  <div className="my-booking-top">
                    <div className="my-booking-school">{b.schoolName || 'Daycare Centre'}</div>
                    <span className="my-booking-status" style={{ background: statusColor + '22', color: statusColor, border: `1px solid ${statusColor}` }}>
                      {STATUS_LABEL[b.status] || b.status}
                    </span>
                  </div>
                  <div className="my-booking-body">
                    <div className="my-booking-details">
                      <div><span>👶</span><span>{b.childName}{b.childAge ? `, ${b.childAge} yrs` : ''}</span></div>
                      <div><span>📅</span><span>{b.date}</span></div>
                      <div><span>🕐</span><span>{b.timeSlot}</span></div>
                      {b.notes && <div><span>📝</span><span>{b.notes}</span></div>}
                    </div>
                    {b.totalPayable > 0 && (
                      <div className="my-booking-total">
                        <div className="my-booking-total-label">Total Payable</div>
                        <div className="my-booking-total-amount">₹{b.totalPayable}</div>
                        {b.pricePerHour > 0 && <div className="my-booking-total-rate">₹{b.pricePerHour}/hr</div>}
                      </div>
                    )}
                  </div>
                  <div className="my-booking-actions">
                    {b.status === 'accepted' && b.liveStreamUrl && (
                      <button className="my-booking-live-btn" onClick={() => setLivePopup(b.liveStreamUrl)}>
                        🔴 Live
                      </button>
                    )}
                    {canCancel && (
                      <button className="my-booking-cancel-btn" onClick={() => cancelBooking(b.id || b.bookingId)}>
                        Cancel Booking
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      )}

      {/* ══ LIVE STREAM POPUP ══ */}
      {livePopup && (
        <div className="live-popup-overlay" onClick={() => setLivePopup(null)}>
          <div className="live-popup-box" onClick={e => e.stopPropagation()}>
            <div className="live-popup-header">
              <span>🔴 Live Stream</span>
              <button className="live-popup-close" onClick={() => setLivePopup(null)}>✕</button>
            </div>
            <div className="live-popup-frame">
              <iframe
                src={toEmbedUrl(livePopup)}
                title="Live Stream"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}

      {/* ══ ABOUT US ══ */}
      {activeSection === 'about' && (
      <section id="about" className="home-about-section">
        <div className="home-schools-inner">
          <div className="home-section-header">
            <h2 className="home-section-title">About LittleAura</h2>
            <p className="home-section-sub">Nurturing little lives with love, safety and transparency</p>
          </div>
          <div className="home-vm-grid" style={{ marginBottom: 48 }}>
            <div className="home-vm-card">
              <div className="home-vm-icon">🌟</div>
              <h3>Our Vision</h3>
              <p>To be India's most trusted platform connecting families with premium, safe daycare environments — where every child thrives, every parent finds peace of mind, and every moment matters.</p>
            </div>
            <div className="home-vm-card">
              <div className="home-vm-icon">🎯</div>
              <h3>Our Mission</h3>
              <p>Empowering parents with transparency and trust — through verified centres, real-time CCTV access, and seamless booking — while helping daycare providers grow their community impact.</p>
            </div>
          </div>
          <div className="home-about-grid">
            <div className="home-about-story">
              <h3>Our Story</h3>
              <p>LittleAura was born from a simple belief: every child deserves a safe, loving and stimulating environment when parents are at work. We saw the anxiety parents face every day — leaving their little ones behind — and decided to change that.</p>
              <p>By partnering with only the finest, verified daycare centres and equipping families with real-time CCTV access and transparent booking, we've built a platform where trust is not just promised — it's proven.</p>
              <p>Today, LittleAura serves hundreds of families across India, and we're just getting started.</p>
            </div>
            <div className="home-about-values">
              <div className="home-about-value"><span className="home-about-val-icon">🔒</span><div><h4>Safety First</h4><p>Every partner centre is audited for hygiene, safety and staff credentials before listing.</p></div></div>
              <div className="home-about-value"><span className="home-about-val-icon">👁️</span><div><h4>Radical Transparency</h4><p>Parents get real-time CCTV access so you always know your child is safe and happy.</p></div></div>
              <div className="home-about-value"><span className="home-about-val-icon">💜</span><div><h4>Built with Love</h4><p>Our team understands the trust you place in us — because we're parents too.</p></div></div>
              <div className="home-about-value"><span className="home-about-val-icon">📱</span><div><h4>Seamless Experience</h4><p>Book a spot, manage visits and communicate with centres — all in minutes.</p></div></div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* ══ PARTNER PROGRAM ══ */}
      {activeSection === 'partner' && (
      <section id="partner" className="home-partner-section">
        <div className="home-schools-inner">
          <div className="home-section-header">
            <h2 className="home-section-title home-section-title-white">Enroll Your Daycare With Us</h2>
            <p className="home-section-sub home-section-sub-white">Join the LittleAura partner network and grow your centre's reach</p>
          </div>
          <div className="home-partner-grid">
            <div className="home-partner-benefit"><span>📣</span><h4>Wider Visibility</h4><p>Get discovered by thousands of parents actively searching for quality daycare in your area.</p></div>
            <div className="home-partner-benefit"><span>📅</span><h4>Easy Booking Management</h4><p>Manage all visit bookings, approvals and schedules from a simple, dedicated dashboard.</p></div>
            <div className="home-partner-benefit"><span>⭐</span><h4>Build Trust &amp; Reputation</h4><p>Showcase your safety standards, facilities and parent reviews to stand out.</p></div>
            <div className="home-partner-benefit"><span>🚀</span><h4>Grow Your Enrolments</h4><p>Turn visit bookings into enrolments with our streamlined parent-to-centre flow.</p></div>
          </div>
          <div className="home-partner-cta-wrap">
            <button className="home-partner-cta" onClick={() => openAuth('register')}>Register as a Daycare Partner →</button>
            <p className="home-partner-note">Free to join · No commission · No hidden fees</p>
          </div>
        </div>
      </section>
      )}

      {/* ══ CONTACT US ══ */}
      {activeSection === 'contact' && (
      <section id="contact" className="home-contact-section">
        <div className="home-schools-inner">
          <div className="home-section-header">
            <h2 className="home-section-title">Contact Us</h2>
            <p className="home-section-sub">We're here to help — reach out anytime</p>
          </div>
          <div className="home-contact-grid">
            <div className="home-contact-card"><div className="home-contact-icon">📧</div><h4>Email Us</h4><p>hello@mylittleaura.in</p></div>
            <div className="home-contact-card"><div className="home-contact-icon">📞</div><h4>Phone</h4><p>076720 27024</p></div>
            <div className="home-contact-card"><div className="home-contact-icon">📍</div><h4>Address</h4><p>Plot No 7-197, NRI Colony, Kakatiya Hills, Pragathi Nagar, Nizampet, Hyderabad, Telangana 500090</p></div>
            <div className="home-contact-card"><div className="home-contact-icon">⏰</div><h4>Support Hours</h4><p>Mon – Sat &nbsp;|&nbsp; 9 AM – 6 PM</p></div>
          </div>
          <div className="home-contact-cta-wrap">
            <a href="https://wa.me/917672027024?text=Hi%20Little%20Aura%2C%20I%20have%20a%20query" target="_blank" rel="noopener noreferrer" className="home-whatsapp-btn">
              <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22" aria-hidden="true">
                <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.345.223-.643.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.463 1.065 2.876 1.213 3.074.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z"/>
              </svg>
              Chat with us on WhatsApp
            </a>
          </div>
        </div>
      </section>
      )}

      {/* ── Footer ── */}
      <footer className="home-footer">
        <div className="home-footer-links">
          <button onClick={() => setActiveSection('home')}>Home</button>
          <button onClick={() => setActiveSection('preschools')}>Pre Schools</button>
          <button onClick={() => setActiveSection('about')}>About Us</button>
          <button onClick={() => setActiveSection('partner')}>Partner Program</button>
          <button onClick={() => setActiveSection('contact')}>Contact Us</button>
        </div>
        <img src="https://firebasestorage.googleapis.com/v0/b/daycare-39d32.firebasestorage.app/o/images%2Flogo_la.png?alt=media" alt="LittleAura" className="home-footer-logo" />
        <p>❤️ Where Little Hearts are Safe, Happy and Inspired to Grow</p>
        <p style={{ fontSize: 12, opacity: .6, marginTop: 6 }}>© {new Date().getFullYear()} LittleAura · mylittleaura.in</p>
      </footer>

      <BookingForm isOpen={!!bookSchool} onClose={() => setBookSchool(null)} onSubmit={submitBooking} school={bookSchool} loading={bookLoading} />
      <AuthModal isOpen={authModal.open} onClose={closeAuth} onLogin={handleLoginSuccess} />
    </div>
  );
}
